import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import clientService from '../services/clientService';

/**
 * ClientStageManager Component
 * 
 * Allows clients to create, edit, delete, and reorder their custom interview stages
 */

const ClientStageManager = ({ pipelineId, onClose, onUpdate }) => {
  const [stages, setStages] = useState([]);
  const [newStageName, setNewStageName] = useState('');
  const [editingStage, setEditingStage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadStages();
  }, [pipelineId]);

  const loadStages = async () => {
    try {
      const data = await clientService.getInterviewStages(pipelineId);
      setStages(data);
    } catch (err) {
      console.error('Error loading stages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStage = async () => {
    if (!newStageName.trim()) return;

    setSaving(true);
    try {
      await clientService.createInterviewStage(pipelineId, newStageName, '');
      setNewStageName('');
      await loadStages();
      onUpdate();
    } catch (err) {
      console.error('Error creating stage:', err);
      alert('Failed to create stage');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStage = async (stageId, newName) => {
    setSaving(true);
    try {
      const stage = stages.find(s => s.id === stageId);
      await clientService.updateInterviewStage(stageId, newName, stage.position);
      setEditingStage(null);
      await loadStages();
      onUpdate();
    } catch (err) {
      console.error('Error updating stage:', err);
      alert('Failed to update stage');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm('Are you sure you want to delete this stage? You can only delete stages with no candidates.')) {
      return;
    }

    setSaving(true);
    try {
      await clientService.deleteInterviewStage(stageId);
      await loadStages();
      onUpdate();
    } catch (err) {
      console.error('Error deleting stage:', err);
      alert(err.response?.data?.error || 'Failed to delete stage');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    if (source.index === destination.index) return;

    // Reorder stages locally
    const reorderedStages = Array.from(stages);
    const [movedStage] = reorderedStages.splice(source.index, 1);
    reorderedStages.splice(destination.index, 0, movedStage);

    // Update positions
    const updatedStages = reorderedStages.map((stage, index) => ({
      ...stage,
      position: index
    }));

    setStages(updatedStages);

    // Save to backend
    setSaving(true);
    try {
      for (const stage of updatedStages) {
        await clientService.updateInterviewStage(stage.id, stage.name, stage.position);
      }
      onUpdate();
    } catch (err) {
      console.error('Error reordering stages:', err);
      alert('Failed to reorder stages');
      await loadStages(); // Reload to reset order
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return ReactDOM.createPortal(
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }} className="bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="text-sm text-gray-600">Loading stages...</span>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }} className="bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Manage Interview Stages</h3>
            <p className="text-xs text-gray-500 mt-0.5">Create, edit, and reorder your interview stages</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          
          {/* Add New Stage */}
          <div className="p-3 bg-gray-50 border rounded-lg space-y-2">
            <p className="text-xs font-semibold text-gray-700">Add New Stage</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateStage()}
                placeholder="e.g., Technical Round, CEO Interview"
                className="flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                disabled={saving}
              />
              <button
                onClick={handleCreateStage}
                disabled={saving || !newStageName.trim()}
                className="px-4 py-2 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>

          {/* Existing Stages */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Current Stages ({stages.length})</p>
              <p className="text-[10px] text-gray-400">Drag to reorder</p>
            </div>
            
            {stages.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <p className="text-xs text-gray-400">No interview stages yet</p>
                <p className="text-[10px] text-gray-400 mt-1">Add your first stage above</p>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="stages">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="space-y-1.5"
                    >
                      {stages.map((stage, index) => (
                        <Draggable key={stage.id} draggableId={`stage-${stage.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center gap-2 px-3 py-2.5 border rounded-lg transition-colors ${
                                snapshot.isDragging
                                  ? 'bg-blue-50 border-blue-300 shadow-lg'
                                  : 'bg-white border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {/* Drag Handle */}
                              <div {...provided.dragHandleProps} className="cursor-move text-gray-300 hover:text-gray-500 flex-shrink-0">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                </svg>
                              </div>

                              {/* Position Number */}
                              <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full text-[10px] font-semibold text-gray-500">
                                {index + 1}
                              </div>
                              
                              {/* Stage Name (Editable) */}
                              {editingStage === stage.id ? (
                                <input
                                  type="text"
                                  defaultValue={stage.name}
                                  className="flex-1 px-2 py-1 text-sm border border-blue-400 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  onBlur={(e) => {
                                    if (e.target.value.trim()) {
                                      handleUpdateStage(stage.id, e.target.value);
                                    } else {
                                      setEditingStage(null);
                                    }
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter' && e.target.value.trim()) {
                                      handleUpdateStage(stage.id, e.target.value);
                                    }
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <p className="flex-1 text-xs font-medium text-gray-900 cursor-pointer hover:text-blue-600 truncate"
                                  onClick={() => setEditingStage(stage.id)}>
                                  {stage.name}
                                </p>
                              )}

                              {/* Actions */}
                              <div className="flex gap-1 flex-shrink-0">
                                {editingStage !== stage.id && (
                                  <button onClick={() => setEditingStage(stage.id)}
                                    className="p-1 text-gray-400 hover:text-blue-600 rounded" title="Edit">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </button>
                                )}
                                <button onClick={() => handleDeleteStage(stage.id)} disabled={saving}
                                  className="p-1 text-gray-400 hover:text-red-500 rounded" title="Delete">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t">
          <button onClick={onClose}
            className="w-full px-4 py-2 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClientStageManager;